import React from "react";
import { View, Text, StyleSheet, TextStyle } from "react-native";
import { colors, spacing, typographyScale, typographyStyles } from "../../../styles/design-system";

export interface Column<T> {
  key: string;
  header: string;
  flex?: number;
  textAlign?: "left" | "center" | "right";
  alignItems?: "flex-start" | "center" | "flex-end";
  renderCell: (item: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
}

function DataTable<T>({ columns, data, keyExtractor }: DataTableProps<T>) {
  return (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        {columns.map((column) => (
          <View
            key={column.key}
            style={[
              styles.tableHeaderCellContainer,
              { flex: column.flex ?? 1 },
              column.textAlign === "right" && styles.alignRight,
              column.textAlign === "center" && styles.alignCenter,
            ]}>
            <Text style={styles.tableHeaderCell}>{column.header}</Text>
          </View>
        ))}
      </View>
      {/* Table Body */}
      {data.map((item, index) => (
        <View key={keyExtractor(item)} style={[styles.tableRow, index < data.length - 1 && styles.tableRowBorder]}>
          {columns.map((column) => {
            const cellContent = column.renderCell(item, index);
            const isReactElement = React.isValidElement(cellContent);

            if (isReactElement) {
              return (
                <View
                  key={column.key}
                  style={[
                    styles.tableCellContainer,
                    { flex: column.flex ?? 1 },
                    column.textAlign === "right" && styles.alignRight,
                    column.textAlign === "center" && styles.alignCenter,
                    column.alignItems && { alignItems: column.alignItems },
                  ]}>
                  {cellContent}
                </View>
              );
            }

            return (
              <View
                key={column.key}
                style={[
                  styles.tableCellContainer,
                  { flex: column.flex ?? 1 },
                  column.textAlign === "right" && styles.alignRight,
                  column.textAlign === "center" && styles.alignCenter,
                ]}>
                <Text style={styles.tableCellText}>{cellContent as string | number}</Text>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

// Styled text components for use in renderCell
export const CellText = ({ children, style }: { children: React.ReactNode; style?: TextStyle }) => (
  <Text style={[styles.tableCellText, style]}>{children}</Text>
);

export const CellTextBold = ({ children, style }: { children: React.ReactNode; style?: TextStyle }) => (
  <Text style={[styles.tableCellBold, style]}>{children}</Text>
);

const styles = StyleSheet.create({
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  tableHeaderCellContainer: {
    paddingVertical: 12,
    paddingHorizontal: spacing.sm,
  },
  tableHeaderCell: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  tableRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  tableCellContainer: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  tableCellText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[600],
  },
  tableCellBold: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  alignRight: {
    alignItems: "flex-end",
  },
  alignCenter: {
    alignItems: "center",
  },
});

export default DataTable;
